/**
 * Validation utilities for group portal.
 *
 * Provides reusable validation functions for forms and inputs used throughout
 * the authentication flows.
 */

import { VALIDATION_RULES, PASSWORD_REQUIREMENTS } from './constants'

/**
 * Group ids look like `emp-grp-nBUmfL9E2e` or, for ASO groups,
 * `aso-grp-PwCAvLx2v4cn76kjQACnR0kJ44OIBL` (suffix lengths differ).
 */
export const GROUP_ID_PATTERN = /^(?:emp|aso)-grp-[A-Za-z0-9]{6,64}$/

export interface ValidationResult {
  isValid: boolean
  error?: string
}

/**
 * Validates an email address.
 *
 * Checks for non-empty value and valid email format.
 */
export function validateEmail(email: string): ValidationResult {
  const trimmed = email.trim()

  if (!trimmed) {
    return { isValid: false, error: 'Email is required' }
  }

  if (!VALIDATION_RULES.EMAIL_REGEX.test(trimmed)) {
    return { isValid: false, error: 'Please enter a valid email address' }
  }

  return { isValid: true }
}

/**
 * Validates a password for basic requirements.
 *
 * Checks for non-empty value and minimum length.
 */
export function validatePassword(password: string): ValidationResult {
  if (!password) {
    return { isValid: false, error: 'Password is required' }
  }

  if (password.length < VALIDATION_RULES.PASSWORD_MIN_LENGTH) {
    return {
      isValid: false,
      error: `Password must be at least ${VALIDATION_RULES.PASSWORD_MIN_LENGTH} characters`,
    }
  }

  return { isValid: true }
}

/** Validates password with full complexity requirements. */
export function validatePasswordComplexity(password: string): ValidationResult {
  // First run basic validation (non-empty, min length)
  const basicResult = validatePassword(password)
  if (!basicResult.isValid) {
    return basicResult
  }

  if (!PASSWORD_REQUIREMENTS.PATTERNS.LOWERCASE.test(password))
    return {
      isValid: false,
      error: 'Password must contain at least one lowercase character',
    }

  if (!PASSWORD_REQUIREMENTS.PATTERNS.UPPERCASE.test(password))
    return {
      isValid: false,
      error: 'Password must contain at least one uppercase character',
    }

  if (!PASSWORD_REQUIREMENTS.PATTERNS.NUMBER.test(password))
    return {
      isValid: false,
      error: 'Password must contain at least one number',
    }

  if (!PASSWORD_REQUIREMENTS.PATTERNS.SYMBOL.test(password))
    return {
      isValid: false,
      error: `Password must contain at least one special character (${PASSWORD_REQUIREMENTS.ALLOWED_SYMBOLS})`,
    }

  return { isValid: true }
}

/** Validates password confirmation matches the original password. */
export function validatePasswordMatch(
  password: string,
  confirmPassword: string,
): ValidationResult {
  if (!confirmPassword) {
    return { isValid: false, error: 'Please confirm your password' }
  }

  if (password !== confirmPassword) {
    return { isValid: false, error: 'Passwords do not match' }
  }

  return { isValid: true }
}

/** Validates and sanitizes a return URL for safe redirects. */
export function validateReturnUrl(url: string | undefined): string | undefined {
  if (!url) {
    return undefined
  }

  // Must start with "/" to be a relative path
  // Must NOT start with "//" to prevent protocol-relative URLs (//evil.com)
  const isRelativePath = url.startsWith('/') && !url.startsWith('//')

  return isRelativePath ? url : undefined
}
